import axios from 'axios';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaBoxOpen } from 'react-icons/fa';
import { API_BASE, capitalizeFirstLetter } from '../utils/helpers';

// Map a raw status to a soft badge tone (clean light-SaaS palette).
function badgeTone(status) {
  switch (status?.toLowerCase()) {
    case 'paid':
    case 'delivered':
      return 'bg-emerald-50 text-emerald-700';
    case 'pending':
    case 'confirmed':
    case 'in_transit':
    case 'in progress':
    case 'partial':
      return 'bg-amber-50 text-amber-700';
    case 'out_for_delivery':
      return 'bg-sky-50 text-sky-700';
    case 'failure':
    case 'cancelled':
    case 'refunded':
      return 'bg-rose-50 text-rose-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

function Badge({ status, label }) {
  return <span className={`ui-badge ${badgeTone(status)}`}>{label}</span>;
}

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageInfo, setPageInfo] = useState({ prev_link: '', next_link: '', current_type: '' });
  const [intakeMap, setIntakeMap] = useState({});
  const [searchParams, setSearchParams] = useState({
    order_id: '',
    name: '',
    email: '',
    financial_status: '',
    fulfillment_status: '',
  });

  const fetchOrders = useCallback(async (direction, triedFallback = false) => {
    try {
      setLoading(true);
      if (direction) setPageInfo((prev) => ({ ...prev, current_type: direction }));

      const paramsShopify = { limit: 10 };
      if (searchParams?.financial_status) paramsShopify.status = searchParams.financial_status;
      if (searchParams?.name) paramsShopify.search = searchParams.name;
      const res = await axios.get(`${API_BASE}/api/shopify/orders`, { params: paramsShopify });

      setOrders(res.data.orders || []);
      setPageInfo((prev) => ({ ...prev, prev_link: '', next_link: '' }));
      setError('');
    } catch (err) {
      console.error('Error fetching orders:', err);
      if (!triedFallback) {
        try {
          const simple = { limit: 10 };
          if (searchParams?.order_id) simple.order_id = searchParams.order_id;
          if (searchParams?.name) simple.name = searchParams.name;
          if (searchParams?.email) simple.email = searchParams.email;
          if (searchParams?.financial_status) simple.financial_status = searchParams.financial_status;
          if (searchParams?.fulfillment_status) simple.fulfillment_status = searchParams.fulfillment_status;

          const res2 = await axios.get(`${API_BASE}/api/orders`, { params: simple });
          setOrders(res2.data.orders || []);
          setPageInfo((prev) => ({ ...prev, prev_link: res2.data.prev_link || '', next_link: res2.data.next_link || '' }));
          setError('');
          return;
        } catch (e2) {
          console.error('Fallback orders fetch failed:', e2);
        }
      }
      setError('Failed to load orders.');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Merge our own captured customer/delivery info (Shopify redacts PII on the
  // current plan). Read-only; falls back silently if unavailable.
  useEffect(() => {
    const ids = orders.map((o) => String(o.id)).filter(Boolean);
    if (!ids.length) { setIntakeMap({}); return; }
    axios
      .post(`${API_BASE}/api/orders/intake/lookup`, { orderIds: ids })
      .then((r) => setIntakeMap(r.data?.map || {}))
      .catch(() => setIntakeMap({}));
  }, [orders]);

  const fmtAddress = (a) =>
    a ? [a.address1, a.address2, a.city, a.province, a.zip, a.country].filter(Boolean).join(', ') : '';

  const handleSearchChange = (e) =>
    setSearchParams((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPageInfo({});
    fetchOrders();
  };

  const deriveShipment = (order) => {
    const fulfillments = Array.isArray(order.fulfillments) ? order.fulfillments : [];
    const latest = fulfillments.length
      ? [...fulfillments].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0]
      : undefined;
    let raw = '';
    if (order?.fulfillment_status === 'fulfilled') raw = 'delivered';
    else if (order?.fulfillment_status === 'partial') raw = 'in_progress';
    else if (latest?.shipment_status) raw = latest.shipment_status;
    else if (fulfillments.length) raw = 'in progress';
    else raw = 'not shipped';
    return raw;
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Orders</h2>
        <p className="text-sm text-slate-500">Shopify orders across the store.</p>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearchSubmit} className="ui-card mb-5 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <input className="ui-input" type="text" placeholder="Order ID" name="order_id" onChange={handleSearchChange} />
          <input className="ui-input" type="text" placeholder="Customer name" name="name" onChange={handleSearchChange} />
          <input className="ui-input" type="text" placeholder="Email" name="email" onChange={handleSearchChange} />
          <select className="ui-input" name="financial_status" onChange={handleSearchChange} defaultValue="">
            <option value="">Payment status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="refunded">Refunded</option>
          </select>
          <select className="ui-input" name="fulfillment_status" onChange={handleSearchChange} defaultValue="">
            <option value="">Fulfillment status</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="unfulfilled">Unfulfilled</option>
            <option value="partial">Partial</option>
          </select>
        </div>
        <div className="mt-3">
          <button type="submit" className="ui-btn-primary">
            <FaSearch className="text-xs" /> Apply filters
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="ui-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr>
                <th className="w-12">#</th>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Email</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Shipment</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j}><div className="h-4 w-full max-w-[120px] animate-pulse rounded bg-slate-100" /></td>
                    ))}
                  </tr>
                ))}

              {!loading && error && (
                <tr><td colSpan={8} className="py-10 text-center text-rose-600">{error}</td></tr>
              )}

              {!loading && !error && orders.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
                      <FaBoxOpen className="text-2xl" />
                      <p className="text-sm">No orders found.</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && !error && orders.map((order, index) => {
                const raw = deriveShipment(order);
                const intake = intakeMap[String(order.id)] || {};
                const custName =
                  intake.customer_name ||
                  [order.customer?.first_name, order.customer?.last_name].filter(Boolean).join(' ') ||
                  '—';
                const email = intake.email || order.email || '—';
                const addr = fmtAddress(intake.address);
                return (
                  <tr
                    key={order.id}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    className="cursor-pointer"
                  >
                    <td className="text-slate-400">{index + 1}</td>
                    <td className="font-medium text-slate-900">
                      {order.name || order.id}
                      {intake.placeholder && (
                        <span
                          className="ui-badge ml-2 bg-amber-50 text-amber-700"
                          title="This order was placed on an old app version that didn't send the item list. Confirm items with the customer."
                        >
                          ⚠ Items to confirm
                        </span>
                      )}
                    </td>
                    <td title={addr}>
                      <div className="font-medium text-slate-800">{custName}</div>
                      {intake.phone && <div className="text-xs text-slate-400">{intake.phone}</div>}
                    </td>
                    <td className="text-slate-500">{email}</td>
                    <td className="font-medium text-slate-900 whitespace-nowrap">{order.total_price} {order.currency}</td>
                    <td><Badge status={order.financial_status} label={capitalizeFirstLetter(order.financial_status || 'unknown')} /></td>
                    <td><Badge status={raw} label={capitalizeFirstLetter(raw || 'not shipped')} /></td>
                    <td className="whitespace-nowrap text-slate-500">
                      {order.created_at ? new Date(order.created_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <button className="ui-btn-ghost disabled:opacity-40" disabled={!pageInfo.prev_link} onClick={() => fetchOrders('previous')}>
            Previous
          </button>
          <button className="ui-btn-ghost disabled:opacity-40" disabled={!pageInfo.next_link} onClick={() => fetchOrders('next')}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Orders;
